import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

import { Chip, IconSquare } from "@/components/panels";
import { EXPERIMENT_STATUS, INITIATIVE_STATUS, PACE_TONES, PACE_WORDS } from "@/growth/growthMath";

// The growth system's smaller parts: charts drawn to scale, list rows with a 36px icon square, chips for the status
// vocabularies, and the button classes every growth page shares. Built on components/panels.jsx.

export const PRIMARY_BUTTON = "inline-flex h-[38px] items-center justify-center gap-2 rounded-lg bg-sm-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-[#002d6d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-not-allowed disabled:opacity-50";
export const GHOST_BUTTON = "inline-flex h-[34px] items-center justify-center gap-2 rounded-lg border border-sm-ink/15 bg-white px-3.5 text-xs font-bold text-sm-primary transition hover:border-sm-accent/60 hover:bg-sm-accent/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-not-allowed disabled:opacity-50";
export const SMALL_PRIMARY = "inline-flex h-[30px] shrink-0 items-center justify-center gap-1.5 rounded-lg bg-sm-primary px-3 text-xs font-bold text-white transition-colors hover:bg-[#002d6d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-not-allowed disabled:opacity-50";
export const SMALL_GHOST = "inline-flex h-[30px] shrink-0 items-center justify-center gap-1.5 rounded-lg border border-sm-ink/15 bg-white px-3 text-xs font-bold text-sm-primary transition hover:border-sm-accent/60 hover:bg-sm-accent/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-not-allowed disabled:opacity-50";
export const TEXT_LINK = "text-xs font-bold text-sm-primary hover:text-sm-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent rounded";

export const PaceChip = ({ status, testId }) => <Chip tone={PACE_TONES[status] || "quiet"} testId={testId}>{PACE_WORDS[status] || status}</Chip>;
export const InitiativeChip = ({ status }) => <Chip tone={INITIATIVE_STATUS[status]?.tone || "quiet"}>{INITIATIVE_STATUS[status]?.label || status}</Chip>;
export const ExperimentChip = ({ status }) => <Chip tone={EXPERIMENT_STATUS[status]?.tone || "quiet"}>{EXPERIMENT_STATUS[status]?.label || status}</Chip>;

/** A 36px-icon list row. Clickable when `onClick` is given (a real button), otherwise static. */
export function ListRow({ icon, tone = "quiet", title, text, right = null, onClick, testId, className = "" }) {
  const body = (
    <>
      <IconSquare icon={icon} tone={tone} />
      <div className="min-w-0 text-left">
        <div className="text-sm font-bold leading-snug text-sm-ink">{title}</div>
        {text ? <div className="mt-0.5 text-xs leading-relaxed text-sm-ink/65">{text}</div> : null}
      </div>
      {right != null ? <div className="col-start-2 flex flex-wrap items-center gap-2 justify-self-start sm:col-start-3 sm:justify-self-end">{right}</div> : null}
    </>
  );
  const layout = `grid grid-cols-[36px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-2 py-3 sm:grid-cols-[36px_minmax(0,1fr)_auto] ${className}`;
  if (onClick) {
    return (
      <li className="border-t border-sm-ink/[0.08] first:border-t-0" data-testid={testId}>
        <button type="button" onClick={onClick} className={`${layout} w-full rounded-lg text-left transition-colors hover:bg-sm-ink/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent`}>{body}</button>
      </li>
    );
  }
  return <li className={`${layout} border-t border-sm-ink/[0.08] first:border-t-0`} data-testid={testId}>{body}</li>;
}

export const RowList = ({ children, className = "", testId }) => <ul className={`mt-1 ${className}`} data-testid={testId}>{children}</ul>;

/** A quiet note inside a panel: a line of context, or a warning when `tone` is warn. */
export function SectionNote({ tone = "info", children, className = "", testId }) {
  const styles = tone === "warn" ? "border-[#F59E0B]/30 bg-[#F59E0B]/[0.08] text-[#7C4A03]" : tone === "ok" ? "border-[#22C55E]/30 bg-[#22C55E]/[0.08] text-[#166534]" : "border-sm-accent/30 bg-sm-accent/[0.08] text-sm-ink";
  return (
    <div data-testid={testId} className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-[13px] leading-relaxed ${styles} ${className}`}>
      <Info size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** A thin progress meter. */
export function Meter({ percent, tone = "primary", height = 6, className = "" }) {
  const color = tone === "ok" ? "bg-[#16A34A]" : tone === "warn" ? "bg-[#F59E0B]" : tone === "bad" ? "bg-[#EF4343]" : tone === "light" ? "bg-white" : "bg-sm-primary";
  const track = tone === "light" ? "bg-white/20" : "bg-sm-primary/[0.08]";
  return (
    <span className={`block w-full overflow-hidden rounded-full ${track} ${className}`} style={{ height }} role="img" aria-label={`${Math.round(percent || 0)}%`}>
      <span className={`block h-full rounded-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, percent || 0))}%` }} />
    </span>
  );
}

/** The element's width, for charts that draw at the width they get. */
export function useElementWidth(fallback = 600) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => { const next = Math.round(entries[0]?.contentRect?.width || 0); if (next > 0) setWidth(next); });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const finite = (value) => typeof value === "number" && Number.isFinite(value);

/** Seven-ish values as a small line with an area under it; gaps (null) are skipped. */
export function Sparkline({ values, width = 120, height = 32, color = "#4690D9", className = "" }) {
  const known = (values || []).filter(finite);
  if (known.length < 2) return <span className={`inline-block ${className}`} style={{ width, height }} aria-hidden="true" />;
  const min = Math.min(...known);
  const max = Math.max(...known);
  const points = (values || []).map((value, index) => (finite(value) ? [4 + (index * (width - 8)) / Math.max(1, values.length - 1), height - 4 - ((value - min) / (max - min || 1)) * (height - 8)] : null)).filter(Boolean);
  const path = points.map((point, index) => `${index ? "L" : "M"}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(" ");
  const last = points[points.length - 1];
  return (
    <svg className={className} width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={`${path} L${last[0].toFixed(1)} ${height - 2} L${points[0][0].toFixed(1)} ${height - 2} Z`} fill={color} opacity="0.12" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="3" fill={color} stroke="#fff" strokeWidth="1.5" />
    </svg>
  );
}

/**
 * A line chart drawn to scale with a faint grid: `series` = [{ key, label, values: number[]|null[], color, dashed, area }],
 * one x position per index, `labels` under some indexes, an optional horizontal `target` line.
 */
export function LineChart({ series, labels = [], height = 170, yFormat = (value) => String(Math.round(value)), target = null, testId }) {
  const [ref, width] = useElementWidth(600);
  const all = series.flatMap((entry) => entry.values.filter(finite));
  if (target && finite(target.value)) all.push(target.value);
  const count = Math.max(...series.map((entry) => entry.values.length), 2);
  const hasData = all.length > 0;
  const lo = hasData ? Math.min(0, Math.min(...all)) : 0;
  const hi = hasData ? Math.max(...all) * 1.08 || 1 : 1;
  const padL = 46;
  const padR = 12;
  const padT = 10;
  const padB = 22;
  const innerW = Math.max(40, width - padL - padR);
  const innerH = height - padT - padB;
  const x = (index) => padL + (index * innerW) / (count - 1);
  const y = (value) => padT + innerH - ((value - lo) / (hi - lo || 1)) * innerH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => lo + (hi - lo) * fraction);
  return (
    <div ref={ref} className="w-full" data-testid={testId}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={series.map((entry) => entry.label).join(", ")}>
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={padL} x2={width - padR} y1={y(tick)} y2={y(tick)} stroke="rgba(15,36,66,0.08)" />
            <text x={padL - 8} y={y(tick)} dy=".35em" fontSize="10" textAnchor="end" fill="#758188">{yFormat(tick)}</text>
          </g>
        ))}
        {target && finite(target.value) ? (
          <g>
            <line x1={padL} x2={width - padR} y1={y(target.value)} y2={y(target.value)} stroke="#B45309" strokeWidth="1.5" strokeDasharray="4 4" />
            <text x={width - padR} y={y(target.value) - 5} fontSize="10" textAnchor="end" fontWeight="700" fill="#B45309">{target.label || "Target"}</text>
          </g>
        ) : null}
        {series.map((entry) => {
          const points = entry.values.map((value, index) => (finite(value) ? [x(index), y(value)] : null));
          const path = points.map((point, index) => (point ? `${index === 0 || !points[index - 1] ? "M" : "L"}${point[0].toFixed(1)} ${point[1].toFixed(1)}` : "")).join(" ");
          const firstIndex = points.findIndex(Boolean);
          const lastIndex = points.length - 1 - [...points].reverse().findIndex(Boolean);
          const last = points[lastIndex];
          return (
            <g key={entry.key}>
              {entry.area && firstIndex >= 0 && last ? <path d={`${path} L${last[0].toFixed(1)} ${(padT + innerH).toFixed(1)} L${points[firstIndex][0].toFixed(1)} ${(padT + innerH).toFixed(1)} Z`} fill={entry.color} opacity="0.08" /> : null}
              <path d={path} fill="none" stroke={entry.color} strokeWidth="2" strokeLinejoin="round" strokeDasharray={entry.dashed ? "4 4" : undefined} />
              {last && !entry.dashed ? <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="3.5" fill={entry.color} stroke="#fff" strokeWidth="1.5" /> : null}
            </g>
          );
        })}
        {labels.map((label, index) => (label ? <text key={index} x={x(index)} y={height - 6} fontSize="10" textAnchor={index === 0 ? "start" : index === labels.length - 1 ? "end" : "middle"} fill="#758188">{label}</text> : null))}
      </svg>
      {!hasData ? <p className="mt-2 text-xs text-sm-ink/60">No data yet.</p> : null}
    </div>
  );
}

/** Bars for a short series (days of a week, weeks of a plan). The last bar is the current one. */
export function BarChart({ values, labels = [], height = 72, highlightLast = true, color = "#4690D9", testId }) {
  const [ref, width] = useElementWidth(300);
  const known = values.filter(finite);
  const top = known.length ? Math.max(...known) : 1;
  const count = Math.max(values.length, 1);
  const gap = 4;
  const barWidth = (width - gap * (count - 1)) / count;
  const labelHeight = labels.length ? 14 : 0;
  const plotHeight = height - labelHeight;
  return (
    <div ref={ref} className="w-full" data-testid={testId}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Bar chart">
        <line x1="0" x2={width} y1={plotHeight} y2={plotHeight} stroke="rgba(15,36,66,0.12)" />
        {values.map((value, index) => {
          const barHeight = finite(value) ? Math.max(2, (value / (top || 1)) * (plotHeight - 4)) : 0;
          const isLast = index === values.length - 1;
          return <rect key={index} x={(index * (barWidth + gap)).toFixed(1)} y={(plotHeight - barHeight).toFixed(1)} width={barWidth.toFixed(1)} height={barHeight.toFixed(1)} rx="3" fill={isLast && highlightLast ? "#003B8D" : color} opacity={highlightLast && !isLast ? 0.55 : 1} />;
        })}
        {labels.map((label, index) => (label ? <text key={index} x={(index * (barWidth + gap) + barWidth / 2).toFixed(1)} y={height - 2} fontSize="10" textAnchor="middle" fill="#758188">{label}</text> : null))}
      </svg>
    </div>
  );
}

/** A big figure with a tiny label above it, for the top of a page or a dialog. */
export function Figure({ label, value, sub, className = "", testId }) {
  return (
    <div className={className} data-testid={testId}>
      <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-sm-muted">{label}</div>
      <div className="mt-1 font-display text-[26px] font-extrabold leading-none tracking-[-0.02em] text-sm-ink tabular-nums">{value}</div>
      {sub ? <div className="mt-1.5 text-xs text-sm-ink/65">{sub}</div> : null}
    </div>
  );
}
